import React from "react";

import { Group, Rect, Text, Line, Image as KonvaImage } from "react-konva";

import {
  ELEMENT_HEIGHT_IN_PX,
  ROW_HEIGHT_IN_PX,
  RULER_HEIGHT_IN_PX,
} from "../../constants";
import { ElementType, IElement } from "./types/IElement";

const SNAP_THRESHOLD_PX = 8;

interface ElementProps {
  element: IElement;
  allElements: IElement[];
  onElementChange: (element: IElement) => void;
  onElementSelect?: (element: IElement) => void;
  onContextMenu?: (element: IElement, x: number, y: number) => void;
  pixelsPerMillisecond: number;
  numberOfRows: number;
  playheadTimeMs: number;
  videoDurationMs: number;
}

const ELEMENT_COLORS: Record<
  ElementType,
  { fill: string; selected: string; stroke: string }
> = {
  video: {
    fill: "rgba(59, 130, 246, 0.18)",
    selected: "rgba(59, 130, 246, 0.35)",
    stroke: "#3b82f6",
  },
  gif: {
    fill: "rgba(168, 85, 247, 0.18)",
    selected: "rgba(168, 85, 247, 0.35)",
    stroke: "#a855f7",
  },
  text: {
    fill: "rgba(34, 197, 94, 0.18)",
    selected: "rgba(34, 197, 94, 0.35)",
    stroke: "#22c55e",
  },
};

// Visual Waveform Component - Lightweight version
const VisualWaveform = React.memo(function VisualWaveform({
  x,
  y,
  width,
  height,
  color,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}) {
  const [waveformData, setWaveformData] = React.useState<number[]>([]);
  const isInitializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!isInitializedRef.current) {
      const barCount = Math.floor(width / 4);
      const newWaveform = Array.from(
        { length: barCount },
        () => Math.random() * 0.6 + 0.2,
      );
      setWaveformData(newWaveform);
      isInitializedRef.current = true;
    }
  }, [width]);

  if (waveformData.length === 0) {
    return null;
  }

  const barWidth = 3;
  const totalBarWidth = waveformData.length * barWidth;
  const availableSpace = width - totalBarWidth;
  const spacing =
    waveformData.length > 1 ? availableSpace / (waveformData.length - 1) : 0;

  return (
    <Group x={x} y={y}>
      {waveformData.map((amplitude, index) => {
        const barHeight = amplitude * height * 0.8;
        const barX = index * (barWidth + spacing);
        const barY = (height - barHeight) / 2;
        if (barX + barWidth > width) {
          return null;
        }

        return (
          <Rect
            key={`wave-${x}-${y}-${Math.floor(amplitude * 100)}-${Math.floor(barX)}`}
            x={barX}
            y={barY}
            width={barWidth}
            height={barHeight}
            fill={color}
            opacity={0.5}
          />
        );
      })}
    </Group>
  );
});

/**
 * Snaps a time value (ms) to nearby edges: playhead or other elements' start/end.
 * Returns the snapped value and optionally the snap target pixel position for the guide line.
 */
function snapTime(
  timeMs: number,
  pixelsPerMillisecond: number,
  playheadTimeMs: number,
  allElements: IElement[],
  currentElementId: number,
): { snapped: number; snapGuidePx: number | null } {
  const thresholdMs = SNAP_THRESHOLD_PX / pixelsPerMillisecond;

  // Collect snap targets: playhead + all other element edges
  const otherEdges = allElements
    .filter((el) => el.id !== currentElementId)
    .flatMap((el) => [el.start, el.start + el.width]);
  const targets = [playheadTimeMs, ...otherEdges];

  const result = targets.reduce(
    (acc, t) => {
      const dist = Math.abs(timeMs - t);
      if (dist < acc.minDist) {
        return {
          closest: t,
          minDist: dist,
          guidePx: t * pixelsPerMillisecond,
        };
      }
      return acc;
    },
    {
      closest: timeMs,
      minDist: thresholdMs,
      guidePx: null as number | null,
    },
  );

  return { snapped: result.closest, snapGuidePx: result.guidePx };
}

export default function Element({
  element,
  allElements,
  onElementChange,
  onElementSelect,
  onContextMenu,
  pixelsPerMillisecond,
  numberOfRows,
  playheadTimeMs,
  videoDurationMs,
}: ElementProps) {
  const [isResizing, setIsResizing] = React.useState<string | null>(null);
  const [initialElement, setInitialElement] = React.useState<IElement>(element);
  const [snapGuidePx, setSnapGuidePx] = React.useState<number | null>(null);
  const [gifImage, setGifImage] = React.useState<HTMLImageElement | null>(null);

  React.useEffect(() => {
    if (element.thumbnailUrl) {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.src = element.thumbnailUrl;
      img.onload = () => setGifImage(img);
    }
  }, [element.thumbnailUrl]);

  const colors = ELEMENT_COLORS[element.type] || ELEMENT_COLORS.video;
  const elementWidthPx = Math.max(0, element.width * pixelsPerMillisecond);
  const elementXPx = element.start * pixelsPerMillisecond;

  const isCursorNearElementEdge = (e: any, el: IElement): string | null => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    const elX = el.start * pixelsPerMillisecond;
    const elW = el.width * pixelsPerMillisecond;

    // Cap the resize zones so short clips always keep a draggable middle
    // region — otherwise the whole element is "edge" and can only be resized.
    const edge = Math.min(SNAP_THRESHOLD_PX * 1.5, elW / 3);

    if (pos.x >= elX && pos.x <= elX + edge) {
      return "left";
    }
    if (pos.x >= elX + elW - edge && pos.x <= elX + elW) {
      return "right";
    }
    return null;
  };

  const handleClick = (e: any) => {
    e.cancelBubble = true;
    if (onElementSelect) {
      onElementSelect(element);
    }
  };

  const handleRightClick = (e: any) => {
    e.evt.preventDefault();
    e.cancelBubble = true;
    if (onContextMenu) {
      const stage = e.target.getStage();
      const container = stage.container().getBoundingClientRect();
      onContextMenu(
        element,
        e.evt.clientX - container.left,
        e.evt.clientY - container.top,
      );
    }
  };

  const handleDragStart = (e: any) => {
    e.cancelBubble = true;
    setInitialElement(element);
    const resizeDir = isCursorNearElementEdge(e, element);
    setIsResizing(resizeDir);
    onElementChange({
      ...element,
      isDragging: !resizeDir,
      isResizing: !!resizeDir,
    });
  };

  const handleDragMove = (e: any) => {
    let mouseX = e.target.x() / pixelsPerMillisecond;
    const stage = e.target.getStage();
    const pointerPosition = stage.getPointerPosition();
    const currentCursorX = pointerPosition ? pointerPosition.x : 0;

    if (mouseX < 0) {
      mouseX = 0;
      e.target.x(0);
    }

    if (element.isResizing) {
      e.target.y(0);
    }

    if (element.isResizing) {
      if (isResizing === "left") {
        let newStart = initialElement.start + (mouseX - initialElement.start);
        const snapResult = snapTime(
          newStart,
          pixelsPerMillisecond,
          playheadTimeMs,
          allElements,
          element.id,
        );
        newStart = snapResult.snapped;
        setSnapGuidePx(snapResult.snapGuidePx);

        const newWidth =
          initialElement.width + (initialElement.start - newStart);
        if (newWidth > 0) {
          e.target.x(newStart * pixelsPerMillisecond);
          onElementChange({
            ...element,
            start: newStart,
            width: newWidth,
          });
        }
      } else if (isResizing === "right") {
        let newEnd = currentCursorX / pixelsPerMillisecond;
        const snapResult = snapTime(
          newEnd,
          pixelsPerMillisecond,
          playheadTimeMs,
          allElements,
          element.id,
        );
        newEnd = snapResult.snapped;
        setSnapGuidePx(snapResult.snapGuidePx);

        // Don't let the right edge extend past the end of the video.
        if (videoDurationMs > 0) {
          newEnd = Math.min(newEnd, videoDurationMs);
        }

        const newWidth = newEnd - element.start;
        e.target.x(element.start * pixelsPerMillisecond);
        if (newWidth > 0) {
          onElementChange({
            ...element,
            start: element.start,
            width: newWidth,
          });
        }
      }
    } else if (element.isDragging) {
      // Snap the start edge
      const snapStart = snapTime(
        mouseX,
        pixelsPerMillisecond,
        playheadTimeMs,
        allElements,
        element.id,
      );
      // Also check end edge
      const endMs = mouseX + element.width;
      const snapEnd = snapTime(
        endMs,
        pixelsPerMillisecond,
        playheadTimeMs,
        allElements,
        element.id,
      );

      let snappedStart = mouseX;
      let guide: number | null = null;

      // Use whichever edge snaps closer
      const startDist = Math.abs(snapStart.snapped - mouseX);
      const endDist = Math.abs(snapEnd.snapped - endMs);

      if (
        snapStart.snapGuidePx !== null &&
        (snapEnd.snapGuidePx === null || startDist <= endDist)
      ) {
        snappedStart = snapStart.snapped;
        guide = snapStart.snapGuidePx;
      } else if (snapEnd.snapGuidePx !== null) {
        snappedStart = mouseX + (snapEnd.snapped - endMs);
        guide = snapEnd.snapGuidePx;
      }

      setSnapGuidePx(guide);

      if (snappedStart < 0) snappedStart = 0;
      // Keep the element inside the video: its end can't go past the duration.
      if (videoDurationMs > 0) {
        const maxStart = Math.max(0, videoDurationMs - element.width);
        if (snappedStart > maxStart) snappedStart = maxStart;
      }
      e.target.x(snappedStart * pixelsPerMillisecond);

      const newYRelativeToGroup = e.target.getAbsolutePosition().y;

      if (newYRelativeToGroup < RULER_HEIGHT_IN_PX) {
        e.target.absolutePosition({
          y: RULER_HEIGHT_IN_PX,
          x: e.target.getAbsolutePosition().x,
        });
      }

      if (newYRelativeToGroup > ROW_HEIGHT_IN_PX * numberOfRows) {
        const positionY =
          (numberOfRows - 1) * ROW_HEIGHT_IN_PX + RULER_HEIGHT_IN_PX;
        e.target.absolutePosition({
          y: positionY,
          x: e.target.getAbsolutePosition().x,
        });
      }

      onElementChange({
        ...element,
        start: snappedStart,
        isDragging: true,
      });
    }
  };

  const handleDragEnd = (e: any) => {
    setSnapGuidePx(null);
    let newX = Math.max(0, e.target.x() / pixelsPerMillisecond);
    if (videoDurationMs > 0) {
      newX = Math.min(newX, Math.max(0, videoDurationMs - element.width));
      e.target.x(newX * pixelsPerMillisecond);
    }
    let newYRelativeToGroup = e.target.getAbsolutePosition().y;

    newYRelativeToGroup = Math.max(
      RULER_HEIGHT_IN_PX,
      Math.min(newYRelativeToGroup, ROW_HEIGHT_IN_PX * numberOfRows),
    );
    const nearestRow = Math.round(
      (newYRelativeToGroup - RULER_HEIGHT_IN_PX) / ROW_HEIGHT_IN_PX,
    );
    const snappedY = nearestRow * ROW_HEIGHT_IN_PX + RULER_HEIGHT_IN_PX;

    e.target.absolutePosition({
      x: e.target.getAbsolutePosition().x,
      y: snappedY,
    });

    onElementChange({
      ...element,
      row: nearestRow + 1,
      start: newX,
      isDragging: false,
      isResizing: false,
    });
  };

  const handleMouseEnter = (e: any) => {
    if (!element.isDraggable) return;
    const stage = e.target.getStage();
    const resizeDirection = isCursorNearElementEdge(e, element);
    stage.container().style.cursor = resizeDirection ? "ew-resize" : "grab";
    onElementChange({ ...element, isHovered: true });
  };

  const handleMouseLeave = (e: any) => {
    const stage = e.target.getStage();
    stage.container().style.cursor = "default";
    onElementChange({ ...element, isHovered: false });
  };

  const label =
    element.label ||
    element.type.charAt(0).toUpperCase() + element.type.slice(1);

  let shadowBlur = 2;
  if (element.isSelected) shadowBlur = 8;
  else if (element.isHovered) shadowBlur = 6;

  return (
    <Group
      x={elementXPx}
      draggable={element.isDraggable}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onTap={handleClick}
      onContextMenu={handleRightClick}
    >
      {/* Main element rect */}
      <Rect
        x={0}
        y={0}
        width={elementWidthPx}
        height={ELEMENT_HEIGHT_IN_PX}
        fill={element.isSelected ? colors.selected : colors.fill}
        strokeWidth={element.isSelected || element.isHovered ? 2 : 1}
        stroke={
          element.isSelected || element.isHovered
            ? colors.stroke
            : "rgba(0,0,0,0.1)"
        }
        cornerRadius={6}
        shadowBlur={shadowBlur}
        shadowColor={
          element.isSelected || element.isHovered ? colors.stroke : "#ccc"
        }
        shadowOpacity={0.4}
      />

      {/* Waveform visualization - only for video elements */}
      {element.type === "video" && (
        <VisualWaveform
          x={5}
          y={5}
          width={Math.max(0, elementWidthPx - 10)}
          height={ELEMENT_HEIGHT_IN_PX - 10}
          color={colors.stroke}
        />
      )}

      {/* GIF thumbnail + label */}
      {element.type === "gif" && (
        <Group x={4} y={2} listening={false}>
          {gifImage && (
            <Group>
              <Rect
                width={ELEMENT_HEIGHT_IN_PX - 4}
                height={ELEMENT_HEIGHT_IN_PX - 4}
                cornerRadius={4}
                listening={false}
              />
              <KonvaImage
                image={gifImage}
                width={ELEMENT_HEIGHT_IN_PX - 4}
                height={ELEMENT_HEIGHT_IN_PX - 4}
                cornerRadius={4}
                listening={false}
              />
            </Group>
          )}
          {elementWidthPx > 60 && (
            <Text
              x={gifImage ? ELEMENT_HEIGHT_IN_PX + 2 : 0}
              y={(ELEMENT_HEIGHT_IN_PX - 4) / 2 - 6}
              width={Math.max(
                0,
                elementWidthPx - (gifImage ? ELEMENT_HEIGHT_IN_PX + 14 : 8),
              )}
              text={label}
              fontSize={11}
              fill={colors.stroke}
              fontStyle="bold"
              ellipsis
              wrap="none"
              listening={false}
            />
          )}
        </Group>
      )}

      {/* Text type badge + label */}
      {element.type === "text" && (
        <Group x={8} y={ELEMENT_HEIGHT_IN_PX / 2 - 9} listening={false}>
          <Rect
            width={20}
            height={18}
            fill={colors.stroke}
            cornerRadius={3}
            listening={false}
          />
          <Text
            x={0}
            y={3}
            width={20}
            text="T"
            fontSize={11}
            fontStyle="bold"
            fill="#fff"
            align="center"
            listening={false}
          />
          {elementWidthPx > 50 && (
            <Text
              x={26}
              y={3}
              width={Math.max(0, elementWidthPx - 42)}
              text={label}
              fontSize={11}
              fill={colors.stroke}
              fontStyle="bold"
              ellipsis
              wrap="none"
              listening={false}
            />
          )}
        </Group>
      )}

      {/* Element label (only when wide enough and not text/gif - they use centered icon) */}
      {elementWidthPx > 40 && element.type === "video" && (
        <Text
          x={8}
          y={6}
          text={label}
          fontSize={11}
          fontStyle="bold"
          fill={colors.stroke}
          listening={false}
        />
      )}

      {/* Left resize handle */}
      <Rect
        visible={element.isHovered || element.isResizing}
        x={4}
        y={(ELEMENT_HEIGHT_IN_PX - 18) / 2}
        width={3}
        height={18}
        fill={colors.stroke}
        opacity={0.6}
        cornerRadius={1}
        listening={false}
      />
      <Rect
        visible={element.isHovered || element.isResizing}
        x={9}
        y={(ELEMENT_HEIGHT_IN_PX - 18) / 2}
        width={3}
        height={18}
        fill={colors.stroke}
        opacity={0.6}
        cornerRadius={1}
        listening={false}
      />

      {/* Right resize handle */}
      <Rect
        visible={element.isHovered || element.isResizing}
        x={elementWidthPx - 12}
        y={(ELEMENT_HEIGHT_IN_PX - 18) / 2}
        width={3}
        height={18}
        fill={colors.stroke}
        opacity={0.6}
        cornerRadius={1}
        listening={false}
      />
      <Rect
        visible={element.isHovered || element.isResizing}
        x={elementWidthPx - 7}
        y={(ELEMENT_HEIGHT_IN_PX - 18) / 2}
        width={3}
        height={18}
        fill={colors.stroke}
        opacity={0.6}
        cornerRadius={1}
        listening={false}
      />

      {/* Snap guide line */}
      {snapGuidePx !== null && (element.isDragging || element.isResizing) && (
        <Line
          points={[
            snapGuidePx - elementXPx,
            -RULER_HEIGHT_IN_PX - element.row * ROW_HEIGHT_IN_PX,
            snapGuidePx - elementXPx,
            ROW_HEIGHT_IN_PX * numberOfRows,
          ]}
          stroke="#f59e0b"
          strokeWidth={1}
          dash={[4, 4]}
          listening={false}
        />
      )}
    </Group>
  );
}
